import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { DepositSettingsForm, DepositSettingsSummary } from "./DepositSettingsForm";
import { InheritOrCustomize } from "./InheritOrCustomize";

/**
 * Storefront → Payments → Deposits: what a shopper may deposit at face value in this environment,
 * the presets and the least and most, inheriting the platform default until an editor customizes
 * them ("Customize" starts from the default, a tweak of it), with "Use platform default" going
 * back. Checkout refuses an amount outside them.
 */
export function DepositSettingsCard() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfigQuery();
  const [customizing, setCustomizing] = useState(false);
  const save = useMutation(
    trpc.storefront.setDeposit.mutationOptions({
      onSuccess: (saved) => {
        setCustomizing(false);
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success(
          saved.deposit === null ? "Using the platform default." : "Deposit settings saved.",
        );
      },
    }),
  );
  if (!query.data) return null;
  const own = query.data.config.deposit;
  const platformDefault = query.data.deposit.platformDefault;
  const canEdit = can("editor");
  return (
    <InheritOrCustomize
      title="Deposits"
      inherited={own === null}
      customizing={customizing}
      canEdit={canEdit}
      saving={save.isPending}
      onCustomize={() => setCustomizing(true)}
      onCancel={() => setCustomizing(false)}
      onRevert={() => save.mutate({ deposit: null })}
      descriptions={{
        inherited:
          "Shoppers deposit any amount at face value within the platform's limits, with its presets. Customize them for this store.",
        customizing: "Starts from the platform's settings; nothing is saved until you save.",
        customized:
          "This environment's own presets and limits; the platform default no longer applies here.",
      }}
      inheritedSummary={<DepositSettingsSummary deposit={platformDefault} />}
    >
      {(revert) => (
        <DepositSettingsForm
          key={JSON.stringify(own ?? platformDefault)}
          deposit={own ?? platformDefault}
          canEdit={canEdit}
          saving={save.isPending}
          onSave={(deposit) => save.mutateAsync({ deposit })}
          footer={revert}
        />
      )}
    </InheritOrCustomize>
  );
}
